import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FileDropZone } from '@/components/file-drop-zone';
import { campaignWithTrackingLinksSchema } from '@/schema/campaign-schema';
import { fetchPreviewClone, postCampaign } from '@/services/campaign';
import { CheckCircle, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TrackingLinksFormCard } from '@/routes/console/_authenticated/_layout/campaigns/-features/tracking-links-form-card';
import { TextField } from '@/components/text-field';
import { RichTextField } from '@/components/rich-text-field';
import { CustomSlugField } from '@/components/custom-slug-field';
import { Form } from '@/components/ui/form';
import { useAuthenticationState } from '@/hooks/use-authentication';
import { errorHandler } from '@/lib/utils';
import { CampaignModePicker } from '@/routes/console/_authenticated/_layout/campaigns/-features/campaign-mode-picker';
import { FeedbackConfigCard } from '@/routes/console/_authenticated/_layout/campaigns/-features/feedback-config-card';
import { FeedbackQuestionsBuilder } from '@/routes/console/_authenticated/_layout/campaigns/-features/feedback-questions-builder';
import { AutoStoreConfigCard } from '@/routes/console/_authenticated/_layout/campaigns/-features/auto-store-config-card';
import { ShowroomConfigCard } from '@/routes/console/_authenticated/_layout/campaigns/-features/showroom-config-card';
import { TemplateGallery } from '@/features/campaign-templates';
import { FeatureLocked } from '@/components/feature-locked';
import { useSystemTemplates } from '@/features/campaign-templates/hooks/use-templates';
import { CampaignCustomizePanel } from '@/features/page-designs';
/**
 * Pristine form state used both as initial defaults (non-clone) and as the
 * baseline `form.reset()` target when the user re-picks a template — so the
 * previous template's tracking links / showroom config can't leak into the
 * next pick.
 */
const BLANK_FORM_DEFAULTS = {
    name: '',
    description: '',
    customSlug: '',
    mode: 'classic',
    trackingLinks: [],
    storeAutoDetectEnabled: false,
    fallbackUrl: '',
    appStoreUrl: '',
    googlePlayUrl: '',
    feedbackTitle: '',
    feedbackThankYouMessage: '',
    feedbackTelegramChatId: '',
    feedbackCollectName: true,
    feedbackCollectEmail: true,
    feedbackQuestions: [],
    showroomTagline: '',
    showroomCurrency: 'USD',
    showroomCallNumber: '',
    showroomWhatsappNumber: '',
    showroomTelegramHandle: '',
    showroomDirectionsUrl: '',
    createdFromTemplate: undefined,
    pageDesign: undefined,
    backgroundPattern: undefined,
    randomBackgroundPattern: false,
};
export const Route = createFileRoute('/console/_authenticated/_layout/campaigns/new')({
    component: NewCampaignPage,
    validateSearch: (search) => ({
        cloneFrom: search.cloneFrom,
    }),
    loaderDeps: ({ search }) => ({ cloneFrom: search.cloneFrom }),
    loader: ({ deps }) => {
        if (deps.cloneFrom) {
            return fetchPreviewClone(deps.cloneFrom);
        }
        return null;
    },
});
function NewCampaignPage() {
    const { t } = useTranslation();
    const { workspace } = useAuthenticationState();
    const navigate = Route.useNavigate();
    const campaign = Route.useLoaderData();
    const [imageFile, setImageFile] = React.useState();
    const isClonable = Boolean(workspace?.subscription?.plan?.allowClone && campaign);
    const clonedAppStoreUrl = (isClonable && campaign?.trackingLinks?.find((link) => link.context === 'ios')?.targetUrl) || '';
    const clonedGooglePlayUrl = (isClonable && campaign?.trackingLinks?.find((link) => link.context === 'android')?.targetUrl) || '';
    const clonedNonAutoStoreLinks = isClonable && campaign?.trackingLinks
        ? campaign.trackingLinks
            .filter((link) => link.context !== 'ios' && link.context !== 'android' && link.context !== 'desktop')
            .map((link) => ({
            label: link.label,
            targetUrl: link.targetUrl,
            type: link.type || 'url',
        }))
        : [];
    const form = useForm({
        resolver: yupResolver(campaignWithTrackingLinksSchema),
        defaultValues: {
            name: (isClonable && campaign?.name) || '',
            description: (isClonable && campaign?.description) || '',
            // Never carried over by a clone: the slug namespace is
            // platform-wide and unique, so the copy can't have the original's.
            customSlug: '',
            mode: (isClonable && campaign?.mode) || 'classic',
            trackingLinks: isClonable && campaign?.mode !== 'auto_store' && campaign?.trackingLinks
                ? campaign.trackingLinks.map((link) => ({
                    label: link.label,
                    targetUrl: link.targetUrl,
                    type: link.type || 'url',
                }))
                : isClonable && campaign?.mode === 'auto_store'
                    ? clonedNonAutoStoreLinks
                    : [],
            storeAutoDetectEnabled: (isClonable && campaign?.storeAutoDetectEnabled) || false,
            fallbackUrl: (isClonable && campaign?.fallbackUrl) || '',
            appStoreUrl: clonedAppStoreUrl,
            googlePlayUrl: clonedGooglePlayUrl,
            feedbackTitle: (isClonable && campaign?.feedbackTitle) || '',
            feedbackThankYouMessage: (isClonable && campaign?.feedbackThankYouMessage) || '',
            feedbackTelegramChatId: (isClonable && campaign?.feedbackTelegramChatId) || '',
            feedbackCollectName: isClonable && campaign?.feedbackCollectName !== undefined ? campaign.feedbackCollectName : true,
            feedbackCollectEmail: isClonable && campaign?.feedbackCollectEmail !== undefined ? campaign.feedbackCollectEmail : true,
            showroomTagline: (isClonable && campaign?.showroomTagline) || '',
            showroomCurrency: (isClonable && campaign?.showroomCurrency) || 'USD',
            showroomCallNumber: (isClonable && campaign?.showroomCallNumber) || '',
            showroomWhatsappNumber: (isClonable && campaign?.showroomWhatsappNumber) || '',
            showroomTelegramHandle: (isClonable && campaign?.showroomTelegramHandle) || '',
            showroomDirectionsUrl: (isClonable && campaign?.showroomDirectionsUrl) || '',
        },
    });
    const mode = form.watch('mode');
    const createdFromTemplate = form.watch('createdFromTemplate');
    const plan = workspace?.subscription?.plan;
    const proUnlocked = Boolean(plan?.allowClone);
    const { data: systemTemplates = [] } = useSystemTemplates();
    const planFlags = React.useMemo(() => ({
        allowAutoStore: Boolean(plan?.allowAutoStore),
        allowFeedbackCampaigns: Boolean(plan?.allowFeedbackCampaigns),
        allowShowroom: Boolean(plan?.allowShowroom),
    }), [plan?.allowAutoStore, plan?.allowFeedbackCampaigns, plan?.allowShowroom]);
    // The gallery already locks templates whose mode the workspace plan
    // doesn't allow; this mirrors that gate so a programmatic call can't slip
    // past it. Keep mode→flag in sync with TemplateCard + CampaignController.
    const isTemplateLockedForPlan = React.useCallback((template) => {
        if (template.proOnly && !proUnlocked)
            return true;
        switch (template.mode) {
            case 'auto_store':
                return !planFlags.allowAutoStore;
            case 'feedback':
                return !planFlags.allowFeedbackCampaigns;
            case 'showroom':
                return !planFlags.allowShowroom;
            default:
                return false;
        }
    }, [planFlags, proUnlocked]);
    // Cloning a campaign is its own flow (the loader pre-fills the form);
    // hide the template gallery in that case so we don't fight over the form.
    const [templatePicked, setTemplatePicked] = React.useState(isClonable);
    // The post-template form is split into two screens: campaign details
    // (basics, artwork, mode, destinations) then public-page customization
    // (design + background pattern). Same form state spans both — the step
    // only swaps which fields are visible and whether the primary CTA reads
    // "Next" or "Create campaign".
    const [wizardStep, setWizardStep] = React.useState('details');
    // Reset to step 1 anytime the user re-opens the template gallery, so
    // re-picking starts from the natural beginning instead of stranding the
    // user mid-flow on a screen whose context they just blew away.
    React.useEffect(() => {
        if (!templatePicked)
            setWizardStep('details');
    }, [templatePicked]);
    // Modes that have no public page (auto_store is a pure redirect) skip
    // the customize step entirely — there's nothing to design.
    const hasCustomizeStep = mode === 'classic' || mode === 'feedback' || mode === 'showroom';
    // If the user lands on the customize step then flips back to step 1 and
    // switches to a mode without a customize phase (auto_store), avoid
    // leaving them stranded on a step that no longer renders.
    React.useEffect(() => {
        if (!hasCustomizeStep && wizardStep === 'customize')
            setWizardStep('details');
    }, [hasCustomizeStep, wizardStep]);
    const handlePickTemplate = React.useCallback((template) => {
        if (isTemplateLockedForPlan(template)) {
            toast.error(t('mode_picker.upgrade_prompt', { defaultValue: 'Upgrade required' }));
            return;
        }
        // Reset to a clean slate first so a previous pick's tracking links
        // / showroom copy can't leak into the new template (e.g. picking
        // car-dealership → Back → blank used to keep the 3 car cards).
        form.reset(BLANK_FORM_DEFAULTS);
        // `blank` skips pre-filling and just unlocks the form.
        if (template.id !== 'blank') {
            form.setValue('mode', template.mode);
            form.setValue('createdFromTemplate', template.id);
            if (template.tagline) {
                form.setValue('showroomTagline', template.tagline);
            }
            if (template.feedbackTitle) {
                form.setValue('feedbackTitle', template.feedbackTitle);
            }
            if (template.feedbackThankYouMessage) {
                form.setValue('feedbackThankYouMessage', template.feedbackThankYouMessage);
            }
            const campaignDefaults = template.defaults.campaign;
            if (campaignDefaults?.showroomCurrency) {
                form.setValue('showroomCurrency', campaignDefaults.showroomCurrency);
            }
            if (typeof campaignDefaults?.feedbackCollectName === 'boolean') {
                form.setValue('feedbackCollectName', campaignDefaults.feedbackCollectName);
            }
            if (typeof campaignDefaults?.feedbackCollectEmail === 'boolean') {
                form.setValue('feedbackCollectEmail', campaignDefaults.feedbackCollectEmail);
            }
            if (campaignDefaults?.pageDesign) {
                form.setValue('pageDesign', campaignDefaults.pageDesign);
            }
            if (template.defaults.trackingLinks && template.defaults.trackingLinks.length > 0) {
                form.setValue('trackingLinks', template.defaults.trackingLinks.map((link) => ({
                    label: link.label,
                    type: link.type,
                    targetUrl: '',
                    context: link.context ?? 'default',
                    priceLabel: link.priceLabel,
                })));
            }
        }
        setTemplatePicked(true);
    }, [form, isTemplateLockedForPlan, t]);
    const { mutate, isPending } = useMutation({
        mutationFn: postCampaign,
        onSuccess: (data) => {
            toast.success(data.message);
            navigate({ to: `/console/campaigns/${data.campaign.publicId}` });
        },
        onError: errorHandler,
    });
    /**
     * Validate the step-1 fields (everything except page-design, which is
     * optional in the schema anyway) + the imageFile guard, then advance.
     * On failure, RHF renders inline errors and we surface the artwork
     * miss as a toast.
     */
    const handleAdvance = async () => {
        const valid = await form.trigger();
        if (!valid)
            return;
        if (!imageFile) {
            toast.error(t('required.image', { ns: 'validation' }));
            return;
        }
        setWizardStep('customize');
    };
    const onSubmit = (data) => {
        if (!imageFile) {
            toast.error(t('required.image', { ns: 'validation' }));
            return;
        }
        const { appStoreUrl, googlePlayUrl, ...rest } = data;
        if (data.mode === 'auto_store') {
            const builtLinks = [];
            if (appStoreUrl && appStoreUrl.trim() !== '') {
                builtLinks.push({
                    label: 'App Store',
                    targetUrl: appStoreUrl.trim(),
                    type: 'url',
                    context: 'ios',
                });
            }
            if (googlePlayUrl && googlePlayUrl.trim() !== '') {
                builtLinks.push({
                    label: 'Google Play',
                    targetUrl: googlePlayUrl.trim(),
                    type: 'url',
                    context: 'android',
                });
            }
            mutate({ data: { ...rest, trackingLinks: builtLinks }, imageFile });
            return;
        }
        mutate({ data: rest, imageFile });
    };
    return (<div className="p-6 space-y-4 w-full">
            {/* Header */}
            <div className="space-y-1 border-b pb-4">
                <h1 className="text-3xl font-semibold">{t('campaigns.new.title', { defaultValue: 'New campaign' })}</h1>
                <p className="text-sm text-muted-foreground">{t('campaigns.new.description', { ns: 'glossary' })}</p>
            </div>

            {!templatePicked && proUnlocked ? (<TemplateGallery proUnlocked={proUnlocked} planFlags={planFlags} workspaceId={workspace?.publicId} canDeleteWorkspaceTemplates={proUnlocked} onPick={handlePickTemplate}/>) : null}

            {!templatePicked && !proUnlocked ? (<FreePlanTemplateLock onStartBlank={() => {
                const blank = systemTemplates.find((tpl) => tpl.id === 'blank');
                if (blank)
                    handlePickTemplate(blank);
            }}/>) : null}

            {templatePicked && (<>
                    {hasCustomizeStep && (<WizardStepIndicator step={wizardStep} detailsLabel={t('campaigns.wizard.details', { defaultValue: 'Campaign details' })} customizeLabel={t('campaigns.wizard.customize', { defaultValue: 'Customize public page' })}/>)}

                    <Form {...form}>
                        {wizardStep === 'details' && (<>
                        {/* Basics + Artwork side-by-side on wide screens — they're
            independent inputs of similar visual weight, so pairing
            them halves the vertical scroll without crowding either. */}
                        <div className="grid gap-4 lg:grid-cols-2">
                            {/* Campaign Basics */}
                            <Card className="w-full">
                                <CardHeader>
                                    <CardTitle>{t('campaigns.new.title', { ns: 'glossary' })}</CardTitle>
                                    <CardDescription>
                                        {t('campaigns.new.description', { ns: 'glossary' })}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                                        <TextField control={form.control} name="name" placeholder={t('campaigns.placeholders.name', { ns: 'glossary' })} label={t('campaign_name')} disabled={isPending} autoComplete="campaign-name"/>
                                        <CustomSlugField control={form.control} name="customSlug" label={t('campaigns.custom_slug.label', {
                    ns: 'glossary',
                    defaultValue: 'Custom URL',
                })} disabled={isPending}/>
                                        <RichTextField control={form.control} name="description" placeholder={t('campaigns.placeholders.description', { ns: 'glossary' })} label={t('description')} disabled={isPending}/>
                                    </form>
                                </CardContent>
                            </Card>

                            {/* Artwork */}
                            <Card className="w-full">
                                <CardHeader>
                                    <CardTitle>{t('campaigns.new.artwork', { ns: 'glossary' })}</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <FileDropZone images={imageFile ? [imageFile] : []} onDrop={(files) => setImageFile(files[0])} onUpload={(e) => e.target.files && setImageFile(e.target.files[0])} onRemove={() => setImageFile(undefined)}/>

                                    <p className="text-xs text-muted-foreground">
                                        Recommended: 1080×1440, JPG/PNG, under 5MB
                                    </p>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Mode picker — hidden when a non-blank template was picked
            (the template declares the mode; re-asking made every
            post-template screen look identical). The "Back" button
            below lets the user reopen the gallery to switch templates. */}
                        {(!createdFromTemplate || createdFromTemplate === 'blank') && (<Card className="w-full">
                                <CardHeader>
                                    <CardTitle>How will this campaign work?</CardTitle>
                                    <CardDescription>
                                        Pick how scans should be handled. You can change this later.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <CampaignModePicker control={form.control} name="mode" disabled={isPending}/>
                                </CardContent>
                            </Card>)}

                        {/* Auto-store destinations (only when auto_store mode) */}
                        {mode === 'auto_store' && <AutoStoreConfigCard control={form.control} disabled={isPending}/>}

                        {/* Feedback config (only when feedback mode) */}
                        {mode === 'feedback' && (<>
                                <FeedbackConfigCard control={form.control} disabled={isPending} workspaceId={workspace?.publicId}/>
                                <FeedbackQuestionsBuilder control={form.control} name="feedbackQuestions" disabled={isPending}/>
                            </>)}

                        {/* Showroom config (only when showroom mode) */}
                        {mode === 'showroom' && <ShowroomConfigCard control={form.control} disabled={isPending}/>}

                        {/* Tracking links — both classic and showroom modes;
            showroom mode reads each link as a product card with image+price. */}
                        {(mode === 'classic' || mode === 'showroom') && (<TrackingLinksFormCard form={form} isPending={isPending} mode={mode}/>)}
                        </>)}

                        {wizardStep === 'customize' && hasCustomizeStep && (<>
                        {/* Page design + background-animation customizer.
                    Same panel as the /customize tab on the detail
                    page — picker tabs left, live preview iframe
                    right. Form-driven on create (writes to
                    pageDesign / backgroundPattern / randomBackgroundPattern
                    on the form, included in the POST), then takes
                    over via patches once the campaign exists. */}
                        <div className="space-y-2">
                            <div className="flex items-baseline justify-between gap-3">
                                <h2 className="text-base font-semibold">
                                    {t('campaign:page_design.title', { defaultValue: 'Page design' })}
                                </h2>
                                <p className="text-xs text-muted-foreground">
                                    {t('campaign:page_design.description', {
                    defaultValue: 'Pick how the public page looks. Change anytime — applies on the next scan.',
                })}
                                </p>
                            </div>
                            {/* The iframe-driven preview is tall by nature; let
                    it take the screen on this step since there's
                    nothing else competing for vertical space. */}
                            <div className="h-[min(70vh,720px)]">
                                <CampaignCustomizePanel mode={mode} selectedDesignId={form.watch('pageDesign') ?? null} selectedPatternSlug={form.watch('backgroundPattern') ?? null} randomEnabled={Boolean(form.watch('randomBackgroundPattern'))} proUnlocked={proUnlocked} workspaceId={workspace?.publicId} disabled={isPending} onPickDesign={(designId) => form.setValue('pageDesign', designId)} onPickPattern={(slug) => form.setValue('backgroundPattern', slug ?? undefined)} onToggleRandom={(enabled) => form.setValue('randomBackgroundPattern', enabled)} className="h-full"/>
                            </div>
                        </div>
                        </>)}
                    </Form>

                    {/* Action bar — right-aligned so the primary CTA reads as the
        next step. Stacks vertically on mobile so neither button
        shrinks below tappable size. The button trio depends on
        which wizard step we're on:
          - details + has-customize → Back (to gallery) + Next
          - details + no-customize  → Back (to gallery) + Create
          - customize               → Back (to details)  + Create */}
                    <div className="pt-4 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3">
                        <Button size="lg" variant="outline" onClick={() => {
                if (wizardStep === 'customize') {
                    setWizardStep('details');
                    return;
                }
                // Clearing createdFromTemplate ensures the mode picker
                // becomes visible again if the user re-picks `blank`.
                form.setValue('createdFromTemplate', undefined);
                setTemplatePicked(false);
            }} disabled={isPending}>
                            {t('back')}
                        </Button>
                        {wizardStep === 'details' && hasCustomizeStep ? (<Button size="lg" className="sm:min-w-[220px]" onClick={handleAdvance} disabled={isPending}>
                                {t('next', { defaultValue: 'Next' })}
                            </Button>) : (<Button size="lg" className="sm:min-w-[220px]" onClick={form.handleSubmit(onSubmit)} disabled={isPending}>
                                <CheckCircle className="h-4 w-4"/>
                                {t('create_campaign')}
                            </Button>)}
                    </div>
                </>)}
        </div>);
}
/**
 * Two-step progress indicator for the new-campaign wizard. Renders inline
 * (no card chrome) so it sits naturally between the header and the form.
 * The completed step shows a check; the active step bolds.
 */
function WizardStepIndicator({ step, detailsLabel, customizeLabel }) {
    const detailsDone = step === 'customize';
    return (<div className="flex items-center gap-3 text-sm">
            <StepPill index={1} label={detailsLabel} active={step === 'details'} done={detailsDone}/>
            <div className={cn('h-px flex-1', detailsDone ? 'bg-primary' : 'bg-border')}/>
            <StepPill index={2} label={customizeLabel} active={step === 'customize'} done={false}/>
        </div>);
}
function StepPill({ index, label, active, done }) {
    return (<div className="flex items-center gap-2">
            <span className={cn('inline-flex size-6 items-center justify-center rounded-full border text-xs font-medium', done && 'border-primary bg-primary text-primary-foreground', active && !done && 'border-primary text-primary', !active && !done && 'border-border text-muted-foreground')}>
                {done ? <Check className="size-3.5"/> : index}
            </span>
            <span className={cn('truncate', active ? 'font-medium text-foreground' : 'text-muted-foreground')}>
                {label}
            </span>
        </div>);
}
function FreePlanTemplateLock({ onStartBlank }) {
    const { t } = useTranslation('campaign');
    return (<div className="space-y-4">
            <FeatureLocked title={t('template_gallery.lock.title', {
            defaultValue: 'Campaign templates are a Solo+ feature',
        })} description={t('template_gallery.lock.description', {
            defaultValue: 'Upgrade to unlock industry-specific starting points (auto-store, feedback, showroom) and save your own templates for reuse.',
        })} plan="Solo" features={[
            t('template_gallery.lock.feature_industry', {
                defaultValue: 'Industry starter templates for food, retail, services and more',
            }),
            t('template_gallery.lock.feature_save', {
                defaultValue: 'Save your campaign shape as a reusable team template',
            }),
            t('template_gallery.lock.feature_clone', {
                defaultValue: 'Clone any existing campaign in one click',
            }),
        ]}/>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between rounded-lg border bg-card/40 p-4">
                <div className="space-y-1">
                    <p className="text-sm font-medium">
                        {t('template_gallery.start_blank_title', {
            defaultValue: 'Just want to set up a single QR for free?',
        })}
                    </p>
                    <p className="text-xs text-muted-foreground">
                        {t('template_gallery.start_blank_description', {
            defaultValue: 'Skip templates and configure the campaign manually from scratch.',
        })}
                    </p>
                </div>
                <Button variant="outline" onClick={onStartBlank}>
                    {t('template_gallery.start_blank_button', { defaultValue: 'Start blank' })}
                </Button>
            </div>
        </div>);
}
